import React, { useEffect, useState, useMemo } from 'react';
import '../../styles/AdminMbaProjects.scss';
import RichTextEditor from './RichTextEditor';

const emptyForm = {
  title: '',
  department_id: '',
  type_id: '',
  university_id: '',
  abstract: '',
  pdf_file: '',
  doc_file: '',
  price: 0,
  discount_price: null,
  currency: 'IN',
  access_type: 'Free',
  pricing_type: 'Free',
  originalPrice: '',
  discountedPrice: '',
  isDiscountActive: false,
};

const MbaProjectForm = ({
  onSubmit,
  editingProject,
  onCancel,
  departments,
  types,
  universities = [],
  onRefreshCatalog,
}) => {
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editingProject) {
      setFormData({
        title: editingProject.title || '',
        department_id: editingProject.department_id || '',
        type_id: editingProject.type_id || '',
        university_id: editingProject.university_id || '',
        abstract: editingProject.abstract || '',
        pdf_file: editingProject.pdf_file || '',
        doc_file: editingProject.doc_file || '',
        price: editingProject.price ?? 0,
        discount_price: editingProject.discount_price ?? null,
        currency: editingProject.currency || 'IN',
        access_type: editingProject.access_type || 'Free',
        pricing_type: editingProject.pricing_type || 'Free',
        originalPrice: editingProject.originalPrice ?? '',
        discountedPrice: editingProject.discountedPrice ?? '',
        isDiscountActive: editingProject.isDiscountActive ?? false,
      });
    } else {
      setFormData(emptyForm);
    }
  }, [editingProject]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const calculatedDiscount = useMemo(() => {
    const orig = Number(formData.originalPrice);
    const disc = Number(formData.discountedPrice);
    if (orig > 0 && disc > 0 && disc < orig) {
      return Math.round(((orig - disc) / orig) * 100);
    }
    return 0;
  }, [formData.originalPrice, formData.discountedPrice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const origPrice = Number(formData.originalPrice);
    const discPrice = formData.discountedPrice !== '' && formData.discountedPrice !== null ? Number(formData.discountedPrice) : null;
    const isDisc = formData.isDiscountActive;

    if (isNaN(origPrice) || origPrice <= 0) {
      setError('Original Price must be greater than 0');
      setLoading(false);
      return;
    }

    if (isDisc) {
      if (discPrice === null || isNaN(discPrice)) {
        setError('Discounted Price is required when discount is enabled');
        setLoading(false);
        return;
      }
      if (discPrice >= origPrice) {
        setError('Discounted Price must be less than Original Price');
        setLoading(false);
        return;
      }
      if (discPrice < 0) {
        setError('Discounted Price cannot be negative');
        setLoading(false);
        return;
      }
    }

    try {
      await onSubmit({
        ...formData,
        department_id: Number(formData.department_id),
        type_id: Number(formData.type_id),
        university_id: formData.university_id ? Number(formData.university_id) : null,
        originalPrice: origPrice,
        discountedPrice: isDisc ? discPrice : null,
        isDiscountActive: isDisc,
        price: isDisc ? discPrice : origPrice,
        discount_price: isDisc ? discPrice : null,
        currency: formData.currency,
        access_type: formData.access_type,
        pricing_type: formData.pricing_type,
      });
      if (!editingProject) setFormData(emptyForm);
    } catch (err) {
      setError(err.message || 'Failed to save MBA project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="mba-form" onSubmit={handleSubmit}>
      <div className="form-header">
        <div>
          <h3>{editingProject ? 'Edit MBA Project' : 'Add MBA Project'}</h3>
          <p>Manage MBA projects with documents and description.</p>
        </div>
        {editingProject && (
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        )}
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="form-grid">
        <label className="span-2">
          Title
          <input name="title" value={formData.title} onChange={handleChange} required />
        </label>

        <label>
          Specialization
          <select name="department_id" value={formData.department_id} onChange={handleChange} required>
            <option value="">Select Specialization</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>
        </label>

        <label>
          Type
          <select name="type_id" value={formData.type_id} onChange={handleChange} required>
            <option value="">Select Type</option>
            {types.map((type) => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </label>

        <label>
          University
          <select name="university_id" value={formData.university_id} onChange={handleChange}>
            <option value="">Select University (Optional)</option>
            {universities.map((uni) => (
              <option key={uni.id} value={uni.id}>{uni.name}</option>
            ))}
          </select>
        </label>

        <label>
          Original Price (₹)
          <input
            type="number"
            name="originalPrice"
            value={formData.originalPrice}
            onChange={handleChange}
            required
            placeholder="e.g. 999"
            min="1"
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', height: '100%', paddingTop: '24px' }}>
          <input
            type="checkbox"
            name="isDiscountActive"
            checked={formData.isDiscountActive}
            onChange={(e) => setFormData(prev => ({ ...prev, isDiscountActive: e.target.checked }))}
            style={{ width: '18px', height: '18px', margin: 0, cursor: 'pointer' }}
          />
          Enable Discount
        </label>

        {formData.isDiscountActive && (
          <label>
            Discounted Price (₹)
            <input
              type="number"
              name="discountedPrice"
              value={formData.discountedPrice}
              onChange={handleChange}
              required={formData.isDiscountActive}
              placeholder="e.g. 199"
              min="0"
            />
            {calculatedDiscount > 0 && (
              <span style={{ color: '#0f766e', fontWeight: 'bold', fontSize: '11px', marginTop: '4px' }}>
                Instant Preview: {calculatedDiscount}% OFF
              </span>
            )}
          </label>
        )}

        <label className="span-2">
          Description
          <RichTextEditor
            value={formData.abstract}
            onChange={(val) => setFormData(prev => ({ ...prev, abstract: val }))}
            placeholder="Enter project description with formatting..."
          />
        </label>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Saving...' : 'Save MBA Project'}
        </button>
        {!editingProject && (
          <button type="button" className="btn-secondary" onClick={() => setFormData(emptyForm)}>
            Clear
          </button>
        )}
      </div>
    </form>
  );
};

export default MbaProjectForm;
